import React, { useState, useEffect } from 'react';
import { ArrowLeft, Phone, MessageCircle, Heart, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';

const SOSResources = () => {
  const [resources, setResources] = useState(null);
  const [userLanguage, setUserLanguage] = useState('english');

  useEffect(() => {
    // Get user's language preference (you could get this from auth context)
    const loadResources = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_BASE_URL}/api/sos/resources?language=${userLanguage}`);
        
        if (response.ok) {
          const data = await response.json();
          setResources(data.resources);
        }
      } catch (error) {
        console.error('Error loading SOS resources:', error);
      }
    };

    loadResources();
  }, [userLanguage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-rose-100">
      {/* Header */}
      <div className="bg-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Crisis Support Resources</h1>
              <p className="text-white/80">Immediate help is available 24/7</p>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Banner */}
      <div className="bg-red-600 text-white py-4">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-bold">IMMEDIATE EMERGENCY</span>
          </div>
          <p className="text-lg font-semibold">
            If you're in immediate danger, call 911 or go to your nearest emergency room
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Crisis Hotlines */}
        {resources && (
          <div className="space-y-8">
            <Card className="p-6 sm:p-8 border-red-200">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-red-500" />
                Crisis Hotlines (Free & Confidential)
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resources.crisis_hotlines?.map((hotline, index) => (
                  <div key={index} className="bg-white border border-red-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">{hotline.name}</h3>
                    <div className="text-2xl font-bold text-red-600 mb-2">{hotline.number}</div>
                    <p className="text-gray-600 text-sm">{hotline.description}</p>
                    <Button
                      asChild
                      className="w-full mt-4 bg-red-500 hover:bg-red-600"
                    >
                      <a href={`tel:${hotline.number.replace(/[^0-9]/g, '')}`}>
                        Call Now
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </Card>

            {/* Online Resources */}
            {resources.online_resources && (
              <Card className="p-6 sm:p-8 border-blue-200">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <MessageCircle className="w-5 h-5 mr-2 text-blue-500" />
                  Online Support Resources
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {resources.online_resources.map((resource, index) => (
                    <div key={index} className="bg-white border border-blue-200 rounded-lg p-6">
                      <h3 className="font-bold text-gray-900 mb-2">{resource.name}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <Button
                        asChild
                        variant="outline"
                        className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <a href={resource.url} target="_blank" rel="noopener noreferrer">
                          Visit Website
                        </a>
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Important Notice */}
            <Card className="p-6 sm:p-8 bg-rose-50 border-rose-200">
              <h2 className="text-xl font-bold text-rose-900 mb-4">
                💖 Important Message from Constant Love
              </h2>
              <div className="space-y-4 text-rose-800">
                <p>
                  <strong>Your life has value.</strong> You matter. Your feelings are valid, and this difficult moment will pass.
                </p>
                <p>
                  <strong>Luma is here for support, but not for emergencies.</strong> She's an AI companion designed to help with 
                  daily self-love and emotional support, but she cannot replace professional mental health care.
                </p>
                <p>
                  <strong>You deserve professional help.</strong> The resources above connect you with trained professionals 
                  who specialize in crisis support and can provide the immediate help you need.
                </p>
                <p className="font-semibold">
                  Please reach out now. You don't have to go through this alone. 💖
                </p>
              </div>
            </Card>

            {/* Language Toggle */}
            <div className="text-center">
              <div className="inline-flex bg-white rounded-lg border border-gray-300 p-1">
                <button
                  onClick={() => setUserLanguage('english')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    userLanguage === 'english'
                      ? 'bg-rose-500 text-white'
                      : 'text-gray-600 hover:text-rose-600'
                  }`}
                >
                  🇺🇸 English
                </button>
                <button
                  onClick={() => setUserLanguage('spanish')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    userLanguage === 'spanish'
                      ? 'bg-rose-500 text-white'
                      : 'text-gray-600 hover:text-rose-600'
                  }`}
                >
                  🇪🇸 Español
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SOSResources;